import { Injectable, inject, signal } from '@angular/core';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { TenantContext } from './tenant-context';

/** Forme minimale lue de `brandAssets/{tenantId}` (sous-ensemble du type back-office). */
interface TenantBrand {
  colors?: { primary?: string; primaryDeep?: string; bg?: string; paper?: string };
  logoPath?: string;
  version?: number;
}

/**
 * Theming runtime white-label (OTA-friendly). Surcharge les variables CSS
 * `--color-*` que Tailwind v4 émet déjà sur `:root` — AUCUNE réécriture de
 * `tailwind.css`/`styles.scss` (zéro risque de régression mono-marque). En
 * mono-marque (`tenantId` null) : no-op total, le thème par défaut reste.
 *
 * Invariant offline-first : applique d'abord `fallbackTheme` (build-time) avant
 * tout réseau ; un échec de lecture `brandAssets` garde le fallback (jamais de
 * blocage hors-ligne). Phase 10.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly tenantCtx = inject(TenantContext);

  /** URL/chemin du logo de marque (null tant qu'absent). */
  readonly logo = signal<string | null>(null);

  async applyTenantTheme(): Promise<void> {
    const cfg = this.tenantCtx.tenant();
    if (!cfg) return; // mono-marque : thème par défaut inchangé

    this.setVars(cfg.fallbackTheme); // fallback immédiat (jamais d'écran non-marqué)
    try {
      const snap = await getDoc(doc(getFirestore(), `brandAssets/${cfg.tenantId}`));
      const b = snap.data() as TenantBrand | undefined;
      if (b?.colors) this.setVars(b.colors);
      if (b?.logoPath) this.logo.set(b.logoPath);
    } catch {
      // Hors-ligne / absent : on garde le fallback (règle offline-first du repo).
    }
  }

  private setVars(c: {
    primary?: string;
    primaryDeep?: string;
    bg?: string;
    paper?: string;
  }): void {
    const r = document.documentElement.style;
    if (c.primary) {
      r.setProperty('--color-mboka', c.primary);
      r.setProperty('--color-mboka-tint', hexToRgba(c.primary, 0.1));
    }
    if (c.primaryDeep) r.setProperty('--color-mboka-deep', c.primaryDeep);
    if (c.bg) r.setProperty('--color-bg', c.bg);
    if (c.paper) r.setProperty('--color-paper', c.paper);
  }
}

/** Convertit `#rrggbb` en `rgba(r,g,b,a)` (pour les teintes). Renvoie l'entrée si non-hex. */
function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
